/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */

import {useQuery} from '@github-ui/react-query'
import {fetchQuery, graphql, readInlineData, useRelayEnvironment} from 'react-relay'
import type {IEnvironment} from 'relay-runtime'

import type {
  useIssueQueryIssueContent$data,
  useIssueQueryIssueContent$key,
} from './__generated__/useIssueQueryIssueContent.graphql'
import type {useIssueQueryQuery} from './__generated__/useIssueQueryQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueContentFragment = graphql`
  fragment useIssueQueryIssueContent on Issue @inline {
    id
    databaseId
    number
    title
    body
    url
    repository {
      id
      databaseId
      name
      owner {
        login
      }
      isArchived
      isCopilotAgentEnabled
      defaultBranchRef {
        name
      }
    }
    assignees(first: 20) {
      nodes {
        login
      }
    }
    labels(first: 20) {
      nodes {
        name
      }
    }
    milestone {
      title
    }
    issueType {
      name
    }
    projectItems(first: 20) {
      nodes {
        id
        project {
          id
          title
        }
      }
    }
    subIssues(first: 1) {
      totalCount
    }
  }
`

export type Issue = Omit<useIssueQueryIssueContent$data, ' $fragmentType'>

export function readIssueFromFragment(key: useIssueQueryIssueContent$key): Issue {
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData(IssueContentFragment, key)
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueQuery = graphql`
  query useIssueQueryQuery($owner: String!, $name: String!, $number: Int!) {
    repository(owner: $owner, name: $name) {
      issue(number: $number) {
        ...useIssueQueryIssueContent
      }
    }
  }
`

export function useIssueQuery({owner, repo, issueNumber}: {owner?: string; repo?: string; issueNumber?: number}) {
  const environment = useRelayEnvironment()

  const query = useQuery({
    queryKey: ['copilot-immersive-issue', JSON.stringify(environment), owner, repo, issueNumber],
    queryFn: async (): Promise<Issue | null> => {
      if (!owner || !repo || !issueNumber) {
        return null
      }

      return await fetchIssue(environment, owner, repo, issueNumber)
    },
  })

  const forceFetchFromNetwork = async () => {
    if (!owner || !repo || !issueNumber) {
      return null
    }

    // Bypass cache entirely and fetch directly from network
    return await fetchIssue(environment, owner, repo, issueNumber, true)
  }

  return {
    ...query,
    forceFetchFromNetwork,
  }
}

export async function fetchIssue(
  environment: IEnvironment,
  owner: string,
  repo: string,
  issueNumber: number,
  forceNetwork = false,
) {
  try {
    const data = await fetchQuery<useIssueQueryQuery>(
      environment,
      IssueQuery,
      {
        owner,
        name: repo,
        number: issueNumber,
      },
      {fetchPolicy: forceNetwork ? 'network-only' : 'store-or-network'},
    ).toPromise()

    if (data?.repository?.issue == null) {
      return null
    }

    return readIssueFromFragment(data.repository.issue)
  } catch (e) {
    // Because this query fetches a nested resource (repository -> issue),
    // relay will actually throw if the issue is not found, rather than return null.
    // Check for that specific condition here and return null, otherwise re-throw.
    if (
      typeof e === 'object' &&
      e != null &&
      'name' in e &&
      e.name === 'ValidationError' &&
      'message' in e &&
      typeof e.message === 'string' &&
      e.message.includes('NOT_FOUND')
    ) {
      return null
    }

    throw e
  }
}
